import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, AlertTriangle, CheckCircle2, Loader2, RefreshCw, ChevronDown, ChevronRight,
  Mail, Sparkles, Image as ImageIcon, CreditCard, ShieldCheck, Clock,
  XCircle, ArrowLeft, Search,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

// Visual styling per stuck-state — green = healthy, amber = warm-but-bailed,
// red = needs founder follow-up.
const STUCK_STYLES = {
  healthy: { color: 'emerald', label: 'Healthy', icon: CheckCircle2 },
  signed_up_never_used: { color: 'amber', label: 'Signed up, never opened', icon: Clock },
  signed_up_no_ad: { color: 'amber', label: 'Signed up, no ad yet', icon: Clock },
  trial_cap_hit_no_upgrade: { color: 'amber', label: 'Hit trial cap, no upgrade', icon: AlertTriangle },
  admin: { color: 'slate', label: 'Admin', icon: ShieldCheck },
  past_due: { color: 'rose', label: 'Payment failed', icon: XCircle },
  canceled: { color: 'slate', label: 'Cancelled', icon: XCircle },
  unknown: { color: 'slate', label: 'Unknown', icon: Clock },
};

const stuckMeta = (reason) => {
  if (STUCK_STYLES[reason]) return STUCK_STYLES[reason];
  if (reason && reason.startsWith('hit_')) {
    return { color: 'rose', label: reason.replace('hit_', 'Hit ').replace('_', ' '), icon: AlertTriangle };
  }
  return STUCK_STYLES.unknown;
};

const EVENT_ICON = {
  signup_start: Mail,
  signup_complete: CheckCircle2,
  trial_started: ShieldCheck,
  business_profile_created: Users,
  ad_generated: Sparkles,
  image_generated: ImageIcon,
  checkout_start: CreditCard,
  ad_gen_error: AlertTriangle,
  image_gen_error: AlertTriangle,
  checkout_error: AlertTriangle,
};

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const JourneyRow = ({ user: u }) => {
  const [open, setOpen] = useState(false);
  const meta = stuckMeta(u.stuckReason);
  const Icon = meta.icon;
  return (
    <div data-testid={`journey-row-${u.id}`} className="border border-slate-200 rounded-xl bg-white overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full px-4 py-3 flex items-center gap-4 hover:bg-slate-50 transition-colors text-left"
        data-testid={`journey-toggle-${u.id}`}
      >
        {open ? <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />}
        <div className={`w-9 h-9 rounded-full bg-${meta.color}-100 flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 text-${meta.color}-600`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900 text-sm truncate">{u.email}</span>
            {u.isAdmin && <Badge className="bg-slate-200 text-slate-700 text-xs">admin</Badge>}
            <Badge className={`bg-${meta.color}-100 text-${meta.color}-700 border-${meta.color}-200 text-xs font-medium`}>
              {meta.label}
            </Badge>
          </div>
          <div className="text-xs text-slate-500 mt-0.5 truncate">
            {u.businessName || '(no business name)'} · {u.plan || 'free'} · {u.subscriptionStatus || 'n/a'}
            {u.signupSource && <> · src={u.signupSource}</>}
          </div>
        </div>
        <div className="hidden md:flex items-center gap-4 text-xs text-slate-500 flex-shrink-0">
          <div className="text-center">
            <div className={`font-bold ${u.adCount > 0 ? 'text-slate-900' : 'text-slate-400'}`}>{u.adCount}</div>
            <div>ads</div>
          </div>
          <div className="text-center">
            <div className={`font-bold ${u.imageCount > 0 ? 'text-slate-900' : 'text-slate-400'}`}>{u.imageCount}</div>
            <div>imgs</div>
          </div>
          <div className="text-center">
            <div className={`font-bold ${u.errorCount > 0 ? 'text-rose-600' : 'text-slate-400'}`}>{u.errorCount}</div>
            <div>errors</div>
          </div>
          <div className="text-center w-20">
            <div className="text-slate-700">{fmt(u.lastEvent?.at || u.lastLoginAt || u.createdAt)}</div>
            <div>last seen</div>
          </div>
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 bg-slate-50 border-t border-slate-200" data-testid={`journey-timeline-${u.id}`}>
          <div className="grid sm:grid-cols-2 gap-3 mt-3 text-xs">
            <div>
              <span className="text-slate-500">Signed up:</span>{' '}
              <span className="font-semibold text-slate-700">{fmt(u.createdAt)}</span>
            </div>
            <div>
              <span className="text-slate-500">Last login:</span>{' '}
              <span className="font-semibold text-slate-700">{fmt(u.lastLoginAt)}</span>
            </div>
            <div>
              <span className="text-slate-500">Provider:</span>{' '}
              <span className="font-semibold text-slate-700">{u.provider || 'email'}</span>
            </div>
            <div>
              <span className="text-slate-500">Trial ends:</span>{' '}
              <span className="font-semibold text-slate-700">{fmt(u.trialEndsAt)}</span>
            </div>
          </div>
          <div className="mt-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Timeline · {u.eventCount} events</div>
            {(!u.timeline || u.timeline.length === 0) ? (
              <div className="text-sm text-slate-400 italic py-2">No tracked actions yet &mdash; they signed up but have not interacted with the platform.</div>
            ) : (
              <ol className="relative border-l border-slate-300 ml-2 space-y-2">
                {u.timeline.map((e, idx) => {
                  const EIcon = EVENT_ICON[e.type] || Mail;
                  const isError = e.type.endsWith('_error');
                  return (
                    <li key={idx} className="ml-4 relative" data-testid={`event-${u.id}-${idx}`}>
                      <span className={`absolute -left-[22px] top-1 w-3 h-3 rounded-full ring-2 ring-slate-50 ${
                        isError ? 'bg-rose-500' : 'bg-emerald-500'
                      }`} />
                      <div className="flex items-start gap-2">
                        <EIcon className={`w-3.5 h-3.5 mt-0.5 ${isError ? 'text-rose-600' : 'text-slate-500'}`} />
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-mono ${isError ? 'text-rose-700 font-semibold' : 'text-slate-700'}`}>
                            {e.type}
                          </div>
                          {e.meta && Object.keys(e.meta).length > 0 && (
                            <div className="text-xs text-slate-500 break-words mt-0.5">
                              {Object.entries(e.meta).slice(0, 4).map(([k, v]) => (
                                <span key={k} className="mr-3"><span className="text-slate-400">{k}:</span> {typeof v === 'string' ? v.slice(0, 80) : JSON.stringify(v).slice(0, 80)}</span>
                              ))}
                            </div>
                          )}
                          <div className="text-xs text-slate-400 mt-0.5">{fmt(e.at)}</div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CustomerJourneys = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [days, setDays] = useState(30);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = async (d = days) => {
    setRefreshing(true);
    try {
      const res = await api.get(`/admin/user-journeys?days=${d}&limit=200`);
      setData(res.data);
    } catch (e) {
      // intentional: failure surfaces in UI as 'loading' indefinitely; admin can refresh
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    if (user.email?.toLowerCase() !== 'chiragahir@gmail.com') {
      navigate('/dashboard', { replace: true });
      return;
    }
    // Defer to a microtask so the state setter inside load() runs outside
    // the synchronous effect body (satisfies react-hooks/set-state-in-effect).
    Promise.resolve().then(() => load());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const filtered = useMemo(() => {
    if (!data?.users) return [];
    return data.users.filter(u => {
      if (filter === 'errors' && u.errorCount === 0) return false;
      if (filter === 'no_ad' && u.adCount > 0) return false;
      if (filter === 'healthy' && u.stuckReason !== 'healthy') return false;
      if (filter === 'trial_cap' && u.stuckReason !== 'trial_cap_hit_no_upgrade') return false;
      if (search && !(u.email?.toLowerCase().includes(search.toLowerCase()) ||
                       u.businessName?.toLowerCase().includes(search.toLowerCase()) ||
                       u.name?.toLowerCase().includes(search.toLowerCase()))) return false;
      return true;
    });
  }, [data, filter, search]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fdfaf6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const totals = data?.totals || {};
  return (
    <div className="min-h-screen bg-[#fdfaf6]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 py-8">
        <div className="flex items-center gap-3 mb-2">
          <Button variant="ghost" onClick={() => navigate('/admin/analytics')} className="text-slate-600" data-testid="back-to-analytics">
            <ArrowLeft className="w-4 h-4 mr-1" /> Analytics
          </Button>
        </div>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900 flex items-center gap-2">
              <Users className="w-7 h-7 text-orange-500" /> Customer Journeys
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Every signup over the last <b>{days} days</b> with their full event timeline.
              Click any row to expand the timeline — see exactly where each user got stuck.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => { setDays(Number(e.target.value)); load(Number(e.target.value)); }}
              className="h-9 px-3 rounded-lg border border-slate-300 bg-white text-sm font-medium"
              data-testid="days-select"
            >
              <option value={1}>1d</option>
              <option value={7}>7d</option>
              <option value={30}>30d</option>
              <option value={90}>90d</option>
            </select>
            <Button onClick={() => load()} disabled={refreshing} variant="outline" data-testid="refresh-btn">
              {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Counters */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
          {[
            { key: 'all', label: 'Total signups', value: totals.count || 0, color: 'slate', testid: 'filter-all' },
            { key: 'healthy', label: 'Healthy', value: totals.healthy || 0, color: 'emerald', testid: 'filter-healthy' },
            { key: 'no_ad', label: 'No ad yet', value: (totals.signedUpNeverUsed || 0) + (totals.signedUpNoAd || 0), color: 'amber', testid: 'filter-no-ad' },
            { key: 'trial_cap', label: 'Trial cap hit', value: totals.trialCapHit || 0, color: 'amber', testid: 'filter-trial-cap' },
            { key: 'errors', label: 'Hit errors', value: totals.hitErrors || 0, color: 'rose', testid: 'filter-errors' },
          ].map(c => (
            <button
              key={c.key}
              onClick={() => setFilter(c.key)}
              data-testid={c.testid}
              className={`text-left p-4 rounded-xl border transition-all ${
                filter === c.key
                  ? `border-${c.color}-400 bg-${c.color}-50 ring-2 ring-${c.color}-200`
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <div className="text-2xl font-bold text-slate-900">{c.value}</div>
              <div className="text-xs font-medium text-slate-600 mt-1">{c.label}</div>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="mt-6 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email, name or business…"
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            data-testid="search-input"
          />
        </div>

        {/* List */}
        <div className="mt-4 space-y-2" data-testid="journeys-list">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500 bg-white border border-slate-200 rounded-xl">
              {data?.users?.length === 0
                ? 'No signups in this window yet. Once real visitors sign up via your Facebook posts, they will appear here.'
                : 'No users match the current filter.'}
            </div>
          ) : (
            filtered.map(u => <JourneyRow key={u.id} user={u} />)
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerJourneys;
